import { createRouter, createWebHashHistory } from "vue-router";
import LoginView from "@/views/LoginView.vue";
import ContactView from "@/views/ContactView.vue";
import PaymentView from "@/views/PaymentView.vue";
import AboutView from "@/views/AboutView";
import AuthService from "@/services/AuthService";
import APIService from "@/services/APIService";
import eventBus from "@/plugins/eventBus";
import store from "@/store";

const routes = [
  {
    path: "/",
    redirect: "/about",
    meta: { public: true },
  },
  {
    path: "/auth-success",
    name: "auth-success",
    meta: { public: true },
    beforeEnter: async (to, from, next) => {
      try {
        const token = to.query.token;
        if (token) {
          sessionStorage.setItem("auth_token", token);

          const pendingInvite = sessionStorage.getItem("pending_invite_token");
          if (pendingInvite) {
            sessionStorage.removeItem("pending_invite_token");
            next(`/invite/${pendingInvite}`);
            return;
          }

          await store.dispatch("tenancy/fetchUser");
          const result = await store.dispatch("tenancy/initTenant", {
            appName: process.env.VUE_APP_APP_CODE || "wa",
          });
          if (!result?.requiresSelection) {
            await ensureWorkflowAutomation();
            await store.dispatch("fetchTenantDetails");
          }
          eventBus.emit("user-signed-in");
          next(result?.requiresSelection ? "/tenants" : "/kanban");
        } else {
          next("/login?error=no_token");
        }
      } catch (error) {
        console.error("Auth success error:", error);
        next("/login?error=auth_failed");
      }
    },
  },
  {
    path: "/tenants",
    name: "tenants",
    component: () => import("@/views/TenantSelectorView.vue"),
  },
  {
    path: "/tenants/:tenantId/settings",
    name: "tenant-settings",
    component: () => import("@/views/TenantSettingsView.vue"),
  },
  {
    path: "/invite/:token",
    name: "accept-invite",
    meta: { public: true },
    component: () => import("@/views/AcceptInviteView.vue"),
  },
  {
    path: "/kanban",
    name: "kanban-home",
    component: () => import("@/views/KanbanHomeView.vue"),
  },
  {
    path: "/todo",
    name: "todo",
    component: () => import("@/views/TodoView.vue"),
  },
  {
    path: "/kanban/tables",
    name: "tables-home",
    component: () => import("@/views/TablesHomeView.vue"),
  },
  {
    path: "/kanban/views",
    name: "views-home",
    component: () => import("@/views/ViewsHomeView.vue"),
  },
  {
    path: "/kanban/views/new/design",
    name: "view-designer-new",
    component: () => import("@/views/ViewDesignerView.vue"),
  },
  {
    path: "/kanban/views/:viewName/design",
    name: "view-designer",
    component: () => import("@/views/ViewDesignerView.vue"),
  },
  {
    path: "/kanban/views/:viewName",
    name: "view-visualizer",
    component: () => import("@/views/ViewVisualizerView.vue"),
  },
  {
    path: "/kanban/:boardName/design",
    name: "board-designer",
    component: () => import("@/views/BoardDesignerView.vue"),
  },
  {
    path: "/kanban/:boardName/automation",
    redirect: (to) => `/kanban/${to.params.boardName}/design`,
  },
  {
    path: "/kanban/:boardName/automation/:ruleId",
    name: "automation-rule-editor",
    component: () => import("@/views/AutomationRuleEditorView.vue"),
  },
  {
    path: "/kanban/lookup/:lookupName",
    name: "lookup-values",
    component: () => import("@/views/LookupValuesView.vue"),
  },
  {
    path: "/kanban/table/:tableName",
    name: "table-records",
    component: () => import("@/views/TableRecordsView.vue"),
  },
  {
    path: "/kanban/:boardName",
    name: "kanban-board",
    component: () => import("@/views/KanbanBoardView.vue"),
  },
  {
    path: "/login",
    name: "login",
    meta: { public: true },
    component: LoginView,
  },
  {
    path: "/about",
    name: "about",
    meta: { public: true },
    component: AboutView,
  },
  {
    path: "/logout",
    name: "logout",
    beforeEnter: async (to, from, next) => {
      try {
        await AuthService.signOut();
        next("/about");
      } catch (error) {
        console.error("Error signing out:", error);
        next("/about");
      }
    },
  },
  {
    path: "/contact",
    name: "contact",
    meta: { public: true },
    component: ContactView,
  },
  {
    path: "/payment",
    name: "payment",
    meta: { public: true },
    component: PaymentView,
  },
  {
    path: "/subscription-plans",
    name: "subscription-plans",
    component: () => import("@/views/SubscriptionPlansView.vue"),
  },
  {
    path: "/subscription-success",
    name: "subscription-success",
    component: () => import("@/views/SubscriptionSuccessView.vue"),
  },
  {
    path: "/subscription-cancel",
    name: "subscription-cancel",
    component: () => import("@/views/SubscriptionCancelView.vue"),
  },
  {
    path: "/privacy",
    name: "privacy",
    meta: { public: true },
    component: () => import(/* webpackChunkName: "legal" */ "../views/PrivacyPolicyView.vue"),
  },
  {
    path: "/terms",
    name: "terms",
    meta: { public: true },
    component: () => import(/* webpackChunkName: "legal" */ "../views/TermsOfTradeView.vue"),
  },
];

async function ensureWorkflowAutomation() {
  if (store.getters["currentRole"] !== "admin") return;
  try {
    const tenant = store.getters["tenancy/currentTenant"];
    if (!tenant?.tenant_id) return;
    const tenantRes = await APIService.getTenant(tenant.tenant_id);
    const installedApps = tenantRes.data.installed_apps || [];
    if (!installedApps.includes("workflow_automation")) {
      await APIService.installApp("workflow_automation");
    }
  } catch {
    // Tenant fetch failed — not a blocker
  }
}

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.public) {
    next();
    return;
  }

  try {
    if (!AuthService.isAuthenticated()) {
      next("/login");
      return;
    }
    const hasTenant =
      store.getters["tenancy/currentTenant"] || sessionStorage.getItem("current_tenant");
    if (to.path !== "/tenants" && !hasTenant) {
      next("/tenants");
      return;
    }
    if (from.path === "/tenants" && hasTenant) {
      ensureWorkflowAutomation();
      store.dispatch("fetchTenantDetails");
      store.dispatch("tenancy/fetchUser");
    }
    next();
  } catch (error) {
    console.error("Error checking authentication:", error);
    next("/login");
  }
});

export default router;
